/**
 * Lookback N, threshold and baseline for the Vest volume-spike whales, under
 * the "Vest spikes 🐋" toggle in the Levels menu (same rule as the volume-pane
 * 🐋, the 🐱 and the 🪝 spike factor). Numbers commit on blur or Enter
 * (clamped), so typing "30" never passes through a clamped "3".
 */
import React, { useEffect, useState } from 'react';
import { useVestSpikeSettingsStore } from '../store/vestSpikeSettingsStore';
import { SPIKE_LOOKBACK_RANGE, SPIKE_THRESHOLD_RANGE } from '../utils/vestSpikes';
import { clampBaseline } from '../utils/relVolBaseline';
import BaselineHistoryHint from './BaselineHistoryHint';

export const VEST_SPIKE_RULE_HINT =
  "Rel vol = this bar's Vest NQ-PERP volume / its baseline, per 2m bar, on the chart's timeframe. Baseline (same as the CME rule): auto = same Chicago time of day over the last 10 days Vest was open at that slot when 5+ exist, else the previous N bars; time of day only (needs 5+ too, else no marker); or trailing N only. ~16 days of Vest history are loaded for it. Closed periods and the 15 min after a reopen skipped. Closed bars only.";

const inputStyle: React.CSSProperties = { width: 52, fontSize: 11, padding: '1px 4px' };

const VestSpikeSettingsRow: React.FC = () => {
  const lookback = useVestSpikeSettingsStore((s) => s.lookback);
  const threshold = useVestSpikeSettingsStore((s) => s.threshold);
  const setLookback = useVestSpikeSettingsStore((s) => s.setLookback);
  const setThreshold = useVestSpikeSettingsStore((s) => s.setThreshold);
  const baseline = useVestSpikeSettingsStore((s) => s.baseline);
  const setBaseline = useVestSpikeSettingsStore((s) => s.setBaseline);
  const [n, setN] = useState(String(lookback));
  const [x, setX] = useState(String(threshold));
  useEffect(() => setN(String(lookback)), [lookback]);
  useEffect(() => setX(String(threshold)), [threshold]);

  const commitN = () => {
    const v = Number(n);
    if (Number.isFinite(v) && n.trim() !== '') setLookback(v);
    setN(String(useVestSpikeSettingsStore.getState().lookback));
  };
  const commitX = () => {
    const v = Number(x);
    if (Number.isFinite(v) && x.trim() !== '') setThreshold(v);
    setX(String(useVestSpikeSettingsStore.getState().threshold));
  };
  const onKey = (commit: () => void) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit();
  };

  return (
    <div
      className="levels-vp-row"
      style={{ padding: '2px 12px 6px 32px', gap: 10, flexWrap: 'wrap' }}
      data-testid="vest-spike-settings"
      title={VEST_SPIKE_RULE_HINT}
    >
      <label className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        N
        <input
          type="number"
          min={SPIKE_LOOKBACK_RANGE.min}
          max={SPIKE_LOOKBACK_RANGE.max}
          step={1}
          value={n}
          onChange={(e) => setN(e.target.value)}
          onBlur={commitN}
          onKeyDown={onKey(commitN)}
          aria-label="Vest spike lookback bars"
          data-testid="vest-spike-lookback"
          style={inputStyle}
        />
      </label>
      <label className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        ≥
        <input
          type="number"
          min={SPIKE_THRESHOLD_RANGE.min}
          max={SPIKE_THRESHOLD_RANGE.max}
          step={0.05}
          value={x}
          onChange={(e) => setX(e.target.value)}
          onBlur={commitX}
          onKeyDown={onKey(commitX)}
          aria-label="Vest spike relative volume threshold"
          data-testid="vest-spike-threshold"
          style={inputStyle}
        />
        x avg
      </label>
      <label className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        vs
        <select
          value={baseline}
          onChange={(e) => setBaseline(clampBaseline(e.target.value))}
          aria-label="Vest spike baseline"
          data-testid="vest-spike-baseline"
          style={{ fontSize: 11 }}
        >
          <option value="auto">auto</option>
          <option value="tod">time of day</option>
          <option value="trailing">trailing</option>
        </select>
      </label>
      <BaselineHistoryHint venue="vest" baseline={baseline} style={{ flexBasis: '100%' }} />
    </div>
  );
};

export default VestSpikeSettingsRow;
